// VX L4-10 — KdRegionSheetView: 지역 셀렉 시트(GlobalRegionChip 패널)의 «안쪽»(순수 · props 만 · 훅 없음).
//
// KdRegionBar(상시 노출 바)와 한 몸이다:
//   머리  = 바의 1행 그대로 — 「지역」 + 경로(전국 › 시·도 › 시·군). 같은 클래스(.kd-rb-k · .kd-rb-path · .kd-rb-p), 같은 문구·순서.
//   본문  = 좌 시·도 레일(「전국」 맨 위) / 우 그 시·도의 시·군·구 2열(「{시·도} 전체」 맨 앞) — 바의 2행과 같은 순서.
//   레인 I — 광역시·특별시 구·군도 2단으로 열린다(regions 2단 노드). 순서·0곳 숨김은 호출부 orderTops 몫 · 여기는 현장 수만 덧붙인다.
// 상태·직렬화(?r= · 색인 허브 경로)·기억·계측은 GlobalRegionChip 이 기존 region-scope 로 붙인다 — 이 파일에는 상태가 없다.
//
// ⛔ 선택지는 <button> 이다 — `?r=` URL 을 <a href> 로 내보내지 않는다(R14b 규칙 승계).
// 시안 채점(2026-10-12 블라인드): 좌우 분할 21.5 · 가로 스크롤+격자 19.5 · 3열 격자+칩 19.0 · 현행 15.0 → 좌우 분할 정착.
import React from 'react';
import './kd-region-sheet.css';

/** count = regions.site_count(현장 수). null·없음 = 집계 전 → 숫자를 그리지 않는다(0 으로 위장 금지). */
export interface KdSheetLeaf { code: string; label: string; count?: number | null }
export interface KdSheetTop { code: string; label: string; count?: number | null; children: KdSheetLeaf[] }

/** 현장 수 표기 — 숫자만(「현장 …곳」 은 화면 읽기용 숨김 글자). 0·모름 → null(숫자 없음). */
export function sheetCount(n: number | null | undefined): string | null {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n.toLocaleString('en-US') : null;
}

function Count({ n }: { n: number | null | undefined }) {
  const v = sheetCount(n);
  return v ? <span className="kd-rs-n"><span className="e-sr">현장 </span>{v}<span className="e-sr">곳</span></span> : null;
}
export interface KdSheetHit { top: { code: string; label: string; count?: number | null }; leaf: KdSheetLeaf | null }

export const SHEET_NATIONAL = '전국';

export interface KdRegionSheetViewProps {
  tops: KdSheetTop[];
  /** 현재 적용된 지역 — 시·도 코드(전국이면 null) · 시·군 코드/라벨(시·도 전체면 null). */
  topCode: string | null;
  leaf: KdSheetLeaf | null;
  /** 우측에 시·군을 펼쳐 보이는 시·도(레일에서 고른 것 — 아직 적용 전). */
  expandedCode: string | null;
  q: string;
  hits: KdSheetHit[];
  onQuery?: (q: string) => void;
  onExpand?: (topCode: string) => void;
  /** code=null → 전국. (topCode, null) → 시·도 전체. (topCode, leafCode) → 시·군. */
  onPick?: (topCode: string | null, leafCode: string | null) => void;
  onClose?: () => void;
}

export default function KdRegionSheetView({ tops, topCode, leaf, expandedCode, q, hits, onQuery, onExpand, onPick, onClose }: KdRegionSheetViewProps) {
  const curTop = topCode ? tops.find((t) => t.code === topCode) ?? null : null;
  const exp = expandedCode ? tops.find((t) => t.code === expandedCode) ?? null : null;
  const query = q.trim();
  return (
    <>
      <div className="kd-rs-head">
        <span className="kd-rb-k" data-rd="label">지역</span>
        <ol className="kd-rb-path" aria-label="현재 지역">
          <li><button type="button" className="kd-rb-p" aria-current={!curTop ? 'true' : undefined} onClick={() => onPick?.(null, null)}>{SHEET_NATIONAL}</button></li>
          {curTop ? (
            <li><button type="button" className="kd-rb-p" aria-current={!leaf ? 'true' : undefined} onClick={() => onPick?.(curTop.code, null)}>{curTop.label}</button></li>
          ) : null}
          {curTop && leaf ? (
            <li><button type="button" className="kd-rb-p" aria-current="true" onClick={() => onPick?.(curTop.code, leaf.code)}>{leaf.label}</button></li>
          ) : null}
        </ol>
        <button type="button" className="kd-gn-btn" onClick={onClose}>닫기</button>
      </div>

      <div className="kd-rs-q">
        <input
          type="search"
          className="kd-gn-input"
          value={q}
          onChange={(e) => onQuery?.(e.target.value)}
          readOnly={!onQuery}
          placeholder="시·도, 시·군·구 검색 (예: 창원, 해운대)"
          aria-label="지역 검색"
          enterKeyHint="search"
        />
        {query ? (
          hits.length ? (
            <ul className="kd-gn-hits" aria-label="지역 검색 결과">
              {hits.map((h) => (
                <li key={h.top.code + ':' + (h.leaf?.code ?? '*')}>
                  <button type="button" className="kd-gn-opt" onClick={() => onPick?.(h.top.code, h.leaf?.code ?? null)}>
                    <span>{h.leaf ? h.leaf.label : h.top.label}<Count n={h.leaf ? h.leaf.count : h.top.count} /></span>
                    <span className="kd-gn-opt__sub">{h.leaf ? h.top.label : '시·도 전체'}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="kd-gn-empty">「{query}」 지역을 찾지 못했어요 — 시·도나 시·군·구 이름으로 찾아 보세요</p>
          )
        ) : null}
      </div>

      <div className="kd-rs-split">
        <ul className="kd-rs-tops" aria-label="시·도">
          <li>
            <button type="button" className="kd-rs-top" data-on={!exp && !curTop ? 'true' : undefined} aria-current={!curTop ? 'true' : undefined} onClick={() => onPick?.(null, null)}>{SHEET_NATIONAL}</button>
          </li>
          {tops.map((t) => (
            <li key={t.code}>
              <button
                type="button"
                className="kd-rs-top"
                data-on={exp?.code === t.code ? 'true' : undefined}
                aria-current={curTop?.code === t.code ? 'true' : undefined}
                aria-expanded={t.children.length ? exp?.code === t.code : undefined}
                onClick={() => (t.children.length ? onExpand?.(t.code) : onPick?.(t.code, null))}
              >
                <span>{t.label}</span><Count n={t.count} />
              </button>
            </li>
          ))}
        </ul>
        <ul className="kd-rs-leaves" aria-label={exp ? `${exp.label} 시·군·구` : '시·군·구'}>
          {exp ? (
            <>
              <li data-full="">
                <button type="button" className="kd-rs-leaf" data-all="" aria-current={curTop?.code === exp.code && !leaf ? 'true' : undefined} onClick={() => onPick?.(exp.code, null)}><span>{exp.label} 전체</span><Count n={exp.count} /></button>
              </li>
              {exp.children.map((c) => (
                <li key={c.code}>
                  <button type="button" className="kd-rs-leaf" aria-current={leaf?.code === c.code ? 'true' : undefined} onClick={() => onPick?.(exp.code, c.code)}><span>{c.label}</span><Count n={c.count} /></button>
                </li>
              ))}
            </>
          ) : (
            <li className="kd-rs-guide">왼쪽에서 시·도를 고르면 시·군·구 목록이 현장 많은 순으로 여기에 나옵니다. 「{SHEET_NATIONAL}」을 누르면 지역 구분 없이 봅니다.</li>
          )}
        </ul>
      </div>
    </>
  );
}
